import { useMemo, useState } from 'react';
import { formatDayLabel, formatNumber } from '../format';
import { useElementWidth } from './useElementWidth';
import { clampTooltipLeft, niceMax, roundedEndRect, ticksFor } from './chartUtils';
import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';

export interface StackSeries {
  key: string;
  label: string;
  color: string;
}

/**
 * A day's row. Series values are read by key off the same object, so any shape
 * with `day`, `total` and a numeric property per series key fits.
 */
export interface StackPoint {
  day: string;
  total: number;
}

const MARGIN = { top: 12, right: 12, bottom: 30, left: 46 };
const MAX_BAR_WIDTH = 24;
const SEGMENT_GAP = 2;
const CORNER_RADIUS = 4;
const HEIGHT = 260;

/**
 * A stacked daily column chart.
 *
 * `series` is given in stack order, bottom first, and that order is part of the
 * palette's validation — re-run the palette validator before reordering it or
 * swapping a colour.
 */
export function StackedColumnChart({
  data,
  series,
  ariaLabel = 'Daily totals by category',
}: {
  data: StackPoint[];
  series: StackSeries[];
  ariaLabel?: string;
}) {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const value = (point: StackPoint, key: string): number => {
    const raw = (point as unknown as Record<string, unknown>)[key];
    return typeof raw === 'number' ? raw : 0;
  };

  const activeSeries = useMemo(
    () => series.filter((item) => data.some((point) => value(point, item.key) > 0)),
    [data, series],
  );

  const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const max = niceMax(Math.max(...data.map((point) => point.total), 0));
  const bandWidth = data.length > 0 ? plotWidth / data.length : plotWidth;
  const barWidth = Math.max(Math.min(bandWidth - 4, MAX_BAR_WIDTH), 2);
  const toY = (value: number) => MARGIN.top + plotHeight - (value / max) * plotHeight;

  // Roughly one label per 70px, so dense ranges thin out instead of colliding.
  const labelStride = Math.max(1, Math.ceil(data.length / Math.max(1, Math.floor(plotWidth / 70))));

  const active = activeIndex === null ? null : data[activeIndex];

  function handlePointer(event: React.PointerEvent<SVGSVGElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left - MARGIN.left;
    if (x < 0 || x > plotWidth || data.length === 0) {
      setActiveIndex(null);
      return;
    }
    setActiveIndex(Math.min(data.length - 1, Math.max(0, Math.floor(x / bandWidth))));
  }

  function handleKey(event: React.KeyboardEvent<SVGSVGElement>) {
    if (data.length === 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = activeIndex === null ? (delta > 0 ? 0 : data.length - 1) : activeIndex + delta;
      setActiveIndex(Math.max(0, Math.min(data.length - 1, next)));
    } else if (event.key === 'Escape') {
      setActiveIndex(null);
    }
  }

  if (data.length === 0) {
    return <p className="empty">Nothing in this range.</p>;
  }

  return (
    <div className="chart" ref={ref}>
      <Legend entries={activeSeries.map((item) => ({ label: item.label, color: item.color }))} />
      <svg
        height={HEIGHT}
        role="img"
        tabIndex={0}
        aria-label={`${ariaLabel}, ${data.length} days. Use arrow keys to step through days.`}
        onPointerMove={handlePointer}
        onPointerLeave={() => setActiveIndex(null)}
        onKeyDown={handleKey}
        onBlur={() => setActiveIndex(null)}
      >
        {ticksFor(max).map((tick) => (
          <g key={tick}>
            <line
              className="gridline"
              x1={MARGIN.left}
              x2={MARGIN.left + plotWidth}
              y1={toY(tick)}
              y2={toY(tick)}
            />
            <text className="axis-label" x={MARGIN.left - 8} y={toY(tick) + 4} textAnchor="end">
              {formatNumber(Math.round(tick))}
            </text>
          </g>
        ))}

        {active && activeIndex !== null ? (
          <line
            className="crosshair"
            x1={MARGIN.left + activeIndex * bandWidth + bandWidth / 2}
            x2={MARGIN.left + activeIndex * bandWidth + bandWidth / 2}
            y1={MARGIN.top}
            y2={MARGIN.top + plotHeight}
          />
        ) : null}

        {data.map((point, index) => {
          const x = MARGIN.left + index * bandWidth + (bandWidth - barWidth) / 2;
          const topSeries = [...activeSeries].reverse().find((item) => value(point, item.key) > 0);
          let cumulative = 0;

          return (
            <g key={point.day} opacity={activeIndex === null || activeIndex === index ? 1 : 0.62}>
              {activeSeries.map((item) => {
                const seriesValue = value(point, item.key);
                if (seriesValue <= 0) return null;
                const bottomY = toY(cumulative);
                cumulative += seriesValue;
                const topY = toY(cumulative);
                const isBottomSegment = bottomY >= MARGIN.top + plotHeight - 0.5;
                // A 2px gap in the surface colour separates touching segments.
                const height = Math.max(bottomY - topY - (isBottomSegment ? 0 : SEGMENT_GAP), 0.5);
                const y = bottomY - height;

                return item === topSeries ? (
                  <path
                    key={item.key}
                    d={roundedEndRect(x, y, barWidth, height, CORNER_RADIUS, 'top')}
                    fill={item.color}
                  />
                ) : (
                  <rect key={item.key} x={x} y={y} width={barWidth} height={height} fill={item.color} />
                );
              })}
            </g>
          );
        })}

        <line
          className="baseline"
          x1={MARGIN.left}
          x2={MARGIN.left + plotWidth}
          y1={MARGIN.top + plotHeight}
          y2={MARGIN.top + plotHeight}
        />

        {data.map((point, index) =>
          index % labelStride === 0 ? (
            <text
              className="axis-label"
              key={point.day}
              x={MARGIN.left + index * bandWidth + bandWidth / 2}
              y={HEIGHT - 10}
              textAnchor="middle"
            >
              {formatDayLabel(point.day)}
            </text>
          ) : null,
        )}
      </svg>

      {active && activeIndex !== null ? (
        <ChartTooltip
          title={formatDayLabel(active.day)}
          rows={activeSeries.map((item) => ({
            label: item.label,
            value: formatNumber(value(active, item.key)),
            color: item.color,
          }))}
          total={{ label: 'Total', value: formatNumber(active.total) }}
          style={{
            left: clampTooltipLeft(MARGIN.left + activeIndex * bandWidth + bandWidth / 2, width),
            top: 26,
          }}
        />
      ) : null}
    </div>
  );
}
